import { EMOJI_PICKER_WIDTH } from './styles';

export const RECENT_SECTION = { id: 'recent', title: 'Recently used' };

export const CATEGORY_TAB_META = {
  recent: { icon: '\u{1F553}', label: 'Recently used' },
  0: { icon: '\u{1F603}', label: 'Smileys' },
  1: { icon: '\u{1F44B}', label: 'People' },
  3: { icon: '\u{1F98B}', label: 'Nature' },
  4: { icon: '\u{1F354}', label: 'Food' },
  5: { icon: '\u{1F697}', label: 'Travel' },
  6: { icon: '\u{26BD}', label: 'Activity' },
  7: { icon: '\u{1F4A1}', label: 'Objects' },
  8: { icon: '\u{2764}\u{FE0F}', label: 'Symbols' },
  9: { icon: '\u{1F3F3}\u{FE0F}', label: 'Flags' },
};

export const CATEGORY_FALLBACK_ICON = '\u{1F539}';
export const CATEGORY_PREFERRED_ORDER = ['recent', '0', '8', '1', '6', '7', '3', '4', '5', '9'];
export const TOP_TAB_COUNT = 5;
export const TONE_BUTTONS = ['\u{1F590}\u{FE0F}', '\u{1F44B}\u{1F3FB}', '\u{1F44B}\u{1F3FC}', '\u{1F44B}\u{1F3FD}', '\u{1F44B}\u{1F3FE}', '\u{1F44B}\u{1F3FF}'];
export const EMPTY_PICKER_STYLE = { top: 0, left: 0 };

export const getUniqueItems = (items = []) => Array.from(new Set(items));

export const getNormalizedSections = (sections, uniqueEmojis) => {
  if (sections.length > 0) {
    return sections.map((section) => ({
      ...section,
      emojis: getUniqueItems(section.emojis || []),
    }));
  }

  if (uniqueEmojis.length > 0) {
    return [{
      id: 'all',
      title: 'All emoji',
      emojis: uniqueEmojis,
    }];
  }

  return [];
};

export const getAvailableEmojis = (sections) => sections.reduce(
  (allEmojis, section) => allEmojis.concat(section.emojis),
  []
);

export const getTabSections = (emojiSections, recentEmojis) => (
  recentEmojis.length > 0 ? [RECENT_SECTION, ...emojiSections] : emojiSections
);

export const getOrderedTabSections = (tabSections) => {
  const sectionById = tabSections.reduce((map, section) => {
    map[section.id] = section;
    return map;
  }, {});

  const preferredSections = CATEGORY_PREFERRED_ORDER
    .map((id) => sectionById[id])
    .filter(Boolean);

  const remainingSections = tabSections.filter(
    (section) => !CATEGORY_PREFERRED_ORDER.includes(section.id)
  );

  return [...preferredSections, ...remainingSections];
};

export const getPickerPosition = ({ wrapperRect, pickerHeight, viewportWidth, viewportHeight }) => {
  const viewportPadding = 8;
  const estimatedPickerHeight = 352;
  const maxLeft = Math.max(viewportPadding, viewportWidth - EMOJI_PICKER_WIDTH - viewportPadding);
  const left = Math.min(
    maxLeft,
    Math.max(viewportPadding, wrapperRect.right - EMOJI_PICKER_WIDTH)
  );

  const spaceBelow = viewportHeight - wrapperRect.bottom;
  const shouldOpenUp = spaceBelow < estimatedPickerHeight && wrapperRect.top > estimatedPickerHeight;
  const maxTop = Math.max(viewportPadding, viewportHeight - pickerHeight - viewportPadding);
  const top = shouldOpenUp
    ? Math.max(viewportPadding, Math.min(maxTop, wrapperRect.top - pickerHeight - 8))
    : Math.max(viewportPadding, Math.min(maxTop, wrapperRect.bottom + 8));

  return { top, left };
};

export const getSearchScore = ({ emoji, normalizedSearch, searchIndex, recentEmojis }) => {
  const meta = searchIndex[emoji] || {};
  const label = (meta.label || '').toLowerCase();
  const tags = Array.isArray(meta.tags) ? meta.tags.map((tag) => tag.toLowerCase()) : [];
  const order = Number.isFinite(meta.order) ? meta.order : Number.MAX_SAFE_INTEGER;
  let score = 0;

  if (emoji === normalizedSearch) {
    score += 1000;
  }
  if (label === normalizedSearch) {
    score += 700;
  } else if (label.startsWith(normalizedSearch)) {
    score += 500;
  } else if (label.includes(normalizedSearch)) {
    score += 300;
  }

  const startsWithTag = tags.some((tag) => tag.startsWith(normalizedSearch));
  const includesTag = tags.some((tag) => tag.includes(normalizedSearch));

  if (startsWithTag) {
    score += 240;
  } else if (includesTag) {
    score += 120;
  }

  const recentIndex = recentEmojis.indexOf(emoji);
  if (recentIndex >= 0) {
    score += Math.max(80 - recentIndex * 5, 10);
  }

  return { emoji, score, order };
};

export const getCurrentSectionId = (tabSections, sectionRefs, containerRect) => {
  let currentSectionId = '';
  let closestSectionDistance = Number.POSITIVE_INFINITY;

  tabSections.forEach((section) => {
    const node = sectionRefs.current[section.id];
    if (!node) {
      return;
    }

    const distanceFromTop = node.getBoundingClientRect().top - containerRect.top;
    if (distanceFromTop <= 8 && Math.abs(distanceFromTop) < closestSectionDistance) {
      closestSectionDistance = Math.abs(distanceFromTop);
      currentSectionId = section.id;
    }
  });

  if (currentSectionId) {
    return currentSectionId;
  }

  tabSections.forEach((section) => {
    const node = sectionRefs.current[section.id];
    if (!node) {
      return;
    }

    const distanceFromTop = node.getBoundingClientRect().top - containerRect.top;
    if (distanceFromTop > 8 && distanceFromTop < closestSectionDistance) {
      closestSectionDistance = distanceFromTop;
      currentSectionId = section.id;
    }
  });

  return currentSectionId;
};

export const getScrollTargetTop = (container, node) => {
  const containerRect = container.getBoundingClientRect();
  const nodeRect = node.getBoundingClientRect();
  return Math.max(0, container.scrollTop + nodeRect.top - containerRect.top);
};